<template>
    <!-- Who is waiting for which out-of-stock location, busiest location first. Emailed requests stay for 30 days
         so you can see whether the email led to a purchase. -->
    <section class="sa">
        <div class="sa__tiles">
            <div class="sa__tile"><span>Waiting now</span><strong>{{ waitingCount }} {{ waitingCount === 1 ? 'person' : 'people' }}</strong></div>
            <div class="sa__tile"><span>Locations wanted</span><strong>{{ waitingLocations }}</strong></div>
            <div class="sa__tile"><span>Most wanted</span><strong>{{ mostWanted }}</strong></div>
            <div class="sa__tile"><span>Emailed, last 30 days</span><strong>{{ emailedCount }} · {{ boughtCount }} bought</strong></div>
            <button type="button" class="sa__refresh" @click="store.adminLoad()">Refresh</button>
        </div>

        <p v-if="store.adminLoading && !groups.length" class="sa__empty">Loading…</p>
        <p v-else-if="!groups.length" class="sa__empty">Nobody is waiting for stock right now.</p>

        <div v-for="g in groups" :key="g.key" class="sa__group">
            <button type="button" class="sa__head" :aria-expanded="open.has(g.key)" @click="toggle(g.key)">
                <v-icon :icon="open.has(g.key) ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="18"></v-icon>
                <span class="sa__name">{{ g.isp }} · {{ g.city }}{{ g.is_ipv6 ? ' (IPv6)' : '' }}</span>
                <span class="sa__meta">{{ g.country }}</span>
                <span class="sa__meta sa__who">{{ g.preview }}</span>
                <span v-if="g.waiting" class="sa__pill is-wait">{{ g.waiting }} waiting · longest {{ ago(g.oldest) }}</span>
                <span v-if="g.emailed" class="sa__pill is-sent">{{ g.emailed }} emailed{{ g.bought ? ' · ' + g.bought + ' bought' : '' }}</span>
            </button>
            <div v-if="open.has(g.key)" class="sa__tablewrap">
                <table class="sa__table">
                    <thead><tr><th>Email</th><th>Account</th><th>Asked</th><th>Status</th><th>Bought after email</th><th></th></tr></thead>
                    <tbody>
                        <tr v-for="a in g.items" :key="a.id">
                            <td>{{ a.email }}</td>
                            <td>{{ a.user_name || '' }}{{ a.account_email && a.account_email !== a.email ? ' (' + a.account_email + ')' : '' }}</td>
                            <td>{{ when(a.created_at) }}</td>
                            <td>{{ a.notified_at ? 'Emailed ' + when(a.notified_at) + ' (' + a.notified_stock + ' in stock)' : 'Waiting ' + ago(a.created_at) }}</td>
                            <td>{{ a.notified_at ? (a.bought ? 'Yes' : 'Not yet') : '' }}</td>
                            <td><button type="button" class="sa__del" title="Remove this request" @click="store.adminDelete([a.id])"><v-icon icon="mdi-delete-outline" size="18"></v-icon></button></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStockAlertStore } from '@/stores/stockAlert.store';

const store = useStockAlertStore();
const open = ref(new Set());
const toggle = (key) => { const next = new Set(open.value); if (next.has(key)) next.delete(key); else next.add(key); open.value = next; };

const groups = computed(() => {
    const map = new Map();
    for (const a of store.adminAlerts) {
        const key = [a.country, a.isp, a.city, a.is_ipv6 ? 6 : 4].join('|');
        if (!map.has(key)) map.set(key, { key, country: a.country, isp: a.isp, city: a.city, is_ipv6: a.is_ipv6 === true, items: [] });
        map.get(key).items.push(a);
    }
    const list = [...map.values()].map((g) => {
        const waiting = g.items.filter((a) => !a.notified_at);
        const emailed = g.items.filter((a) => a.notified_at);
        const emails = (waiting.length ? waiting : emailed).map((a) => a.email);
        return { ...g, waiting: waiting.length, emailed: emailed.length, bought: emailed.filter((a) => a.bought).length,
            oldest: waiting.length ? waiting.reduce((min, a) => (a.created_at < min ? a.created_at : min), waiting[0].created_at) : null,
            preview: emails.slice(0, 2).join(', ') + (emails.length > 2 ? `, +${emails.length - 2}` : '') };
    });
    return list.sort((a, b) => b.waiting - a.waiting || b.emailed - a.emailed || a.city.localeCompare(b.city));
});

const waitingCount = computed(() => store.adminAlerts.filter((a) => !a.notified_at).length);
const waitingLocations = computed(() => groups.value.filter((g) => g.waiting).length);
const mostWanted = computed(() => { const g = groups.value.find((x) => x.waiting); return g ? `${g.isp} ${g.city} (${g.waiting})` : 'None'; });
const emailedCount = computed(() => store.adminAlerts.filter((a) => a.notified_at).length);
const boughtCount = computed(() => store.adminAlerts.filter((a) => a.notified_at && a.bought).length);

const when = (value) => new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const ago = (value) => {
    const mins = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
    if (mins < 60) return `${mins} min`;
    if (mins < 60 * 48) return `${Math.round(mins / 60)} hours`;
    return `${Math.round(mins / 1440)} days`;
};

onMounted(() => store.adminLoad());
</script>

<style scoped>
.sa { color: var(--ui-ink); }
.sa__tiles { display: flex; flex-wrap: wrap; align-items: stretch; gap: 8px; margin-bottom: 14px; }
.sa__tile { display: flex; flex-direction: column; padding: 8px 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); }
.sa__tile span { font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--ui-dim); }
.sa__tile strong { font-size: 17px; font-weight: 600; }
.sa__refresh { margin-left: auto; align-self: center; padding: 8px 14px; border: 1px solid var(--ui-line); border-radius: 10px; background: transparent; color: var(--ui-ink); font-size: 13px; cursor: pointer; }
.sa__refresh:hover { border-color: var(--ui-pri-hover); }
.sa__empty { margin: 24px 0; text-align: center; color: var(--ui-dim); }
.sa__group { margin-bottom: 8px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); overflow: hidden; }
.sa__head { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; width: 100%; padding: 10px 12px; border: 0; background: var(--ui-pri-soft); color: var(--ui-ink); text-align: left; cursor: pointer; }
.sa__head:hover { background: var(--ui-card-2); }
.sa__name { font-size: 14px; font-weight: 600; }
.sa__meta { font-size: 12.5px; color: var(--ui-dim); }
.sa__who { flex: 1; min-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sa__pill { padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.sa__pill.is-wait { background: var(--ui-warn-soft); color: var(--ui-warn-ink); }
.sa__pill.is-sent { background: var(--ui-ok-soft); color: var(--ui-ok-ink); }
.sa__tablewrap { overflow-x: auto; }
.sa__table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sa__table th { padding: 8px 12px; font-size: 12px; font-weight: 600; color: var(--ui-dim); text-align: left; white-space: nowrap; }
.sa__table td { padding: 8px 12px; border-top: 1px solid var(--ui-line); }
.sa__del { border: 0; background: transparent; color: var(--ui-dim); cursor: pointer; }
.sa__del:hover { color: var(--ui-ink); }
</style>
